import useDebounce from "@/hooks/useDebounce.ts";
import Badge from "../../../components/Badge";
import Text from "../../../components/Text";
import { Button } from "../../../components/ui/button.tsx";
import { Input } from "../../../components/ui/input.tsx";
import {
  BikeIcon,
  CircleMinus,
  CircleMinusIcon,
  CirclePlusIcon,
  SearchIcon,
  StarIcon,
} from "lucide-react";
import { useEffect, useState, type FC } from "react";
import {
  autoCompleteRestaurantState,
  searchForAutoCompleteRestaurant,
} from "@/store/autoCompleteRestaurantState.ts";
import KrunshScore from "@/components/KrunshScore/index.tsx";
import type { RestaurantFromUrl } from "@/API.ts";
import {
  resetSearchRestaurantWithURLState,
  searchRestaurantWithURL,
  searchRestaurantWithURLState,
} from "@/store/searchRestaurantWithURLState.ts";
import {
  addUserRestaurants,
  resetUserRestaurantsState,
  userAddRestaurantsState,
} from "@/store/userAddRestaurantsState.ts";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

const FindRestaurant: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: restaurantsFound, isLoading: isLoadingAutoComplet } =
    autoCompleteRestaurantState();
  const { data: restaurantUrl, isLoading: isLoadingUrlSearch } =
    searchRestaurantWithURLState();
  const {
    data: addRestaurantsStatus,
    isLoading: isLoadingAddRestaurantsStatus,
  } = userAddRestaurantsState();

  const [searchRestaurant, setSearchRestaurant] = useState("");
  const [urlToSearch, setUrlToSearch] = useState("");
  const [restaurantsSelected, setRestaurantsSelected] = useState<
    RestaurantFromUrl[]
  >([]);

  const searchRestaurantDebounced = useDebounce(searchRestaurant, 300);

  useEffect(() => {
    if (searchRestaurantDebounced) {
      searchForAutoCompleteRestaurant({ search: searchRestaurantDebounced });
    }
  }, [searchRestaurantDebounced]);

  useEffect(() => {
    if (restaurantUrl) {
      setRestaurantsSelected((prevState) => [...prevState, restaurantUrl]);
      resetSearchRestaurantWithURLState();
    }
  }, [restaurantUrl]);

  useEffect(() => {
    if (addRestaurantsStatus) {
      toast.success("Restaurant(s) Ajouté(s)", {
        duration: 2000,
      });
      resetUserRestaurantsState();
      navigate("/");
    }
  }, [addRestaurantsStatus]);

  const unSelectRestaurant = (restaurantId: string) => {
    const copyRestaurantsSelected = [...restaurantsSelected];

    const restaurantToRemove = copyRestaurantsSelected.findIndex(
      ({ id }) => id === restaurantId,
    );

    if (restaurantToRemove !== -1)
      copyRestaurantsSelected.splice(restaurantToRemove, 1);

    setRestaurantsSelected([...copyRestaurantsSelected]);
  };

  return (
    <div className="p-[16px] sm:p-[80px] flex flex-col gap-[40px] overflow-x-hidden w-[full]">
      <div className="w-full flex flex-col justify-center">
        <div className="flex flex-col items-center">
          <Text
            fontWeight={600}
            fontSize={24}
            color="#44546F"
            style={{ lineHeight: "32px" }}
          >
            {t("find_your_restaurant")}
          </Text>
          <Text
            fontWeight={400}
            fontSize={14}
            color="#44546F"
            style={{ lineHeight: "20px" }}
          >
            {t("find_your_restaurant_description")}
          </Text>
        </div>
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-[24px] mt-[40px]">
          <div className="w-full bg-[white] p-[24px] rounded-[24px] flex flex-col gap-[8px]">
            <div className="flex flex-col gap-[8px]">
              <Text
                fontWeight={600}
                fontSize={20}
                style={{ lineHeight: "24px" }}
              >
                {t("quick_search")}
              </Text>
              <Text
                fontWeight={400}
                fontSize={14}
                style={{ lineHeight: "20px" }}
              >
                {t("quick_search_description")}
              </Text>
            </div>

            <Input
              startIcon={SearchIcon}
              placeholder={t("placeholder_search_restaurant_to_add")}
              className="mt-[24px]"
              onChange={(e) => setSearchRestaurant(e.target.value)}
              value={searchRestaurant}
            />
            <div className="flex flex-col gap-[24px] mt-[24px] overflow-y-auto overflow-x-hidden max-h-[300px]">
              {isLoadingAutoComplet && <Text>{t("search_in_progress")}</Text>}
              {restaurantsFound?.map(
                ({
                  krunshScore,
                  id,
                  platform,
                  rating,
                  deliveryTime,
                  name,
                  address,
                }) => (
                  <div
                    key={id}
                    className="bg-[white] rounded-[12px] p-[16px] flex items-center justify-between"
                    style={{
                      boxShadow: "0px 8px 12px 0px #0000000A",
                    }}
                  >
                    <div className="flex items-center gap-[12px] overflow-hidden">
                      <KrunshScore value={krunshScore} />
                      <div>
                        <div className="flex items-center gap-[8px]">
                          <img
                            width={24}
                            height={24}
                            className="rounded-[50%]"
                            src={`/partner/${platform.toLowerCase()}.png`}
                            alt={`logo of ${platform}`}
                          />
                          <Badge>
                            <StarIcon height={16} width={16} color="#758195" />
                            <Text fontWeight={600} fontSize={14}>
                              {rating}
                            </Text>
                          </Badge>
                          <Badge>
                            <BikeIcon height={16} width={16} color="#758195" />
                            <Text fontWeight={600} fontSize={14}>
                              {`${deliveryTime} min`}
                            </Text>
                          </Badge>
                        </div>
                        <div className="flex flex-col mt-[12px]">
                          <Text
                            fontSize={14}
                            fontWeight={600}
                            style={{ lineHeight: "20px" }}
                            className="truncate"
                          >
                            {name}
                          </Text>
                          <Text
                            fontSize={12}
                            fontWeight={400}
                            color="#535862"
                            className="truncate"
                            style={{ lineHeight: "16px" }}
                          >
                            {address}
                          </Text>
                        </div>
                      </div>
                    </div>
                    {restaurantsSelected.find(
                      (restaurant) => id === restaurant.id,
                    ) ? (
                      <CircleMinusIcon
                        color="#1D7AFC"
                        className="cursor-pointer"
                        onClick={() => {
                          unSelectRestaurant(id);
                        }}
                      />
                    ) : (
                      <CirclePlusIcon
                        color="#1D7AFC"
                        className="cursor-pointer"
                        onClick={() => {
                          setRestaurantsSelected((prevState) => [
                            ...prevState,
                            {
                              name,
                              id,
                              platform,
                              __typename: "RestaurantFromUrl",
                            },
                          ]);
                        }}
                      />
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
          <div className="w-full bg-[white] p-[24px] rounded-[24px]">
            <div className="flex flex-col gap-[8px]">
              <Text
                fontWeight={600}
                fontSize={20}
                style={{ lineHeight: "24px" }}
              >
                {t("automatic_import")}
              </Text>
              <Text
                fontWeight={400}
                fontSize={14}
                style={{ lineHeight: "20px" }}
              >
                {t("automatic_import_url")}
              </Text>
            </div>
            <div className="flex flex-col gap-[8px] mt-[24px]">
              <Text
                fontWeight={400}
                fontSize={14}
                style={{ lineHeight: "20px" }}
              >
                {t("automatic_import_paste_url")}
              </Text>
              <div className="flex gap-[16px]">
                <Input
                  value={urlToSearch}
                  onChange={(e) => setUrlToSearch(e.target.value)}
                  placeholder={t("placeholder_import_url_restaurant_to_add")}
                />
                <Button
                  variant="primaryOutlined"
                  onClick={() => searchRestaurantWithURL(urlToSearch)}
                  disabled={isLoadingUrlSearch}
                >
                  {t("analyze")}
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-[8px] mt-[24px]">
              <Text
                fontWeight={400}
                fontSize={14}
                style={{ lineHeight: "20px" }}
              >
                {t("automatic_import_supported_platforms")}
              </Text>
              <div className="flex gap-[8px]">
                <Badge>UberEats</Badge>
                <Badge>Deliveroo</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Text fontWeight={600} fontSize={20} color="black">
          {t("selected_restaurants")}
        </Text>
        <Button
          disabled={
            restaurantsSelected.length === 0 || isLoadingAddRestaurantsStatus
          }
          onClick={() => {
            addUserRestaurants(restaurantsSelected.map(({ id }) => id));
          }}
        >
          {t("validate")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-[8px]">
        {restaurantsSelected.map(({ name, id, platform }) => (
          <div
            key={id}
            className="flex items-center gap-[8px] p-[8px] rounded-[8px] bg-[#F1F2F4] w-[fit-content]"
          >
            <img
              width={16}
              height={16}
              className="rounded-[50%]"
              src={`/partner/${platform.toLowerCase()}.png`}
              alt={`logo of ${platform}`}
            />
            <Text fontSize={14} fontWeight={600}>
              {name}
            </Text>
            <CircleMinus
              height={16}
              width={16}
              color="#758195"
              className="cursor-pointer"
              onClick={() => {
                unSelectRestaurant(id);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FindRestaurant;
